import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { canViewAdminSettings } from '../../permissions';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const FEATURE_TOGGLES = [
  {
    key: 'bid_leveling_enabled',
    title: 'Bid Leveling',
    description: 'Full scope-level bid comparison, sub-contractor tracking, AI extraction, and line-item management. This is the core estimating workflow rebuild — currently under development and not yet validated for production load.',
    status: 'Alpha',
  },
];

export default function AlphaFeaturesPage() {
  const { user, getHeaders } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const fetchFlags = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/settings`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        const map = {};
        data.forEach(s => { map[s.key] = s.value; });
        setSettings(map);
      }
    } catch (e) { /* keep previous */ }
    setLoading(false);
  }, [getHeaders]);

  useEffect(() => {
    if (canViewAdminSettings(user)) fetchFlags();
    else setLoading(false);
  }, [user, fetchFlags]);

  async function toggle(key) {
    const current = settings[key] === 'true';
    const newVal = current ? 'false' : 'true';
    setSaving(key);
    try {
      await fetch(`${API_BASE}/settings/${key}`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ value: newVal, isSecret: false }),
      });
      await fetchFlags();
    } catch (e) { alert('Failed: ' + e.message); }
    setSaving(null);
  }

  if (!canViewAdminSettings(user)) {
    return (
      <div className="page-content">
        <div className="empty-state"><h3>Admin only</h3><p>Feature management requires administrator access.</p></div>
      </div>
    );
  }

  return (
    <>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Link to="/settings" className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>&larr; Settings</Link>
          <h2>Features</h2>
        </div>
      </div>

      <div className="page-content" style={{ maxWidth: '760px' }}>
        <div style={{
          background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px',
          padding: '14px 18px', marginBottom: '20px', fontSize: '13px', color: '#78350f', lineHeight: 1.5,
        }}>
          Feature toggles control visibility of modules across BidBoard.
          When a feature is <strong>OFF</strong>, the UI is hidden and the server rejects API calls to that module.
          When you flip a feature <strong>ON</strong>, it becomes immediately available to all users. Test in Stage first.
        </div>

        {loading && <div style={{ padding: '20px', color: '#777' }}>Loading...</div>}

        <h3 style={{ fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--color-gray-500)', marginBottom: '12px' }}>Feature Toggles</h3>

        {FEATURE_TOGGLES.map(f => {
          const isOn = settings[f.key] === 'true';
          const isSaving = saving === f.key;
          return (
            <div key={f.key} className="card" style={{ marginBottom: '16px' }}>
              <div className="card-body" style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-black)' }}>{f.title}</span>
                    <span style={{
                      fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase',
                      padding: '2px 8px', borderRadius: '4px',
                      background: isOn ? '#dcfce7' : '#f3f4f6',
                      color: isOn ? '#15803d' : '#6b7280',
                    }}>
                      {isOn ? 'ON' : 'OFF'} &middot; {f.status}
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#555', lineHeight: 1.5 }}>{f.description}</div>
                </div>
                <button
                  className={`btn ${isOn ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ minWidth: '70px', flexShrink: 0, marginTop: '4px' }}
                  onClick={() => toggle(f.key)}
                  disabled={isSaving || loading}
                >
                  {isSaving ? '...' : isOn ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
